import React, { useState, useEffect, useRef } from 'react';
import { AlertCircle, Loader2, FileText } from 'lucide-react';

interface DOCXViewerProps {
  fileUrl?: string;
  currentPage: number;
  onPageChange: (page: number) => void;
  onTotalPagesChange: (total: number) => void;
}

export const DOCXViewer: React.FC<DOCXViewerProps> = ({
  fileUrl,
  currentPage,
  onPageChange,
  onTotalPagesChange
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [content, setContent] = useState<string>('');
  const [pages, setPages] = useState<string[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const loadDOCX = async () => {
      if (!fileUrl) {
        setError('No DOCX file available');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // For DOCX files, we'll use mammoth.js to convert to HTML
        const response = await fetch(fileUrl);
        if (!response.ok) {
          throw new Error('Failed to fetch DOCX file');
        }

        const arrayBuffer = await response.arrayBuffer();
        
        // Import mammoth.js dynamically
        const mammoth = await import('mammoth');
        const result = await mammoth.convertToHtml({ arrayBuffer });
        
        if (result.messages.length > 0) {
          console.warn('DOCX conversion warnings:', result.messages);
        }

        const htmlContent = result.value;
        setContent(htmlContent);

        // For simplicity, we'll treat the entire document as one page
        // In a more advanced implementation, you could split by page breaks or sections
        const documentPages = [htmlContent];
        setPages(documentPages);
        onTotalPagesChange(documentPages.length);

        setLoading(false);
      } catch (err) {
        console.error('DOCX loading error:', err);
        setError(err instanceof Error ? err.message : 'Failed to load DOCX document');
        setLoading(false);
      }
    };

    loadDOCX();
  }, [fileUrl, onTotalPagesChange]);

  if (!fileUrl) {
    return (
      <div className="flex items-center justify-center h-64 bg-muted rounded-lg">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
          <p className="text-muted-foreground">No DOCX file available</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-2" />
          <p className="text-muted-foreground">Loading DOCX...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-2" />
          <p className="text-destructive">{error}</p>
        </div>
      </div>
    );
  }

  const currentPageContent = pages[currentPage - 1] || '';

  return (
    <div ref={containerRef} className="w-full">
      <div className="bg-white dark:bg-gray-900 min-h-[400px] p-6 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm">
        <div className="max-w-none prose prose-sm dark:prose-invert">
          <div 
            dangerouslySetInnerHTML={{ __html: currentPageContent }}
            className="docx-content"
          />
        </div>
        
        {pages.length > 0 && (
          <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 text-center">
            <div className="flex items-center justify-center space-x-2 text-sm text-muted-foreground">
              <FileText className="h-4 w-4" />
              <span>Page {currentPage} of {pages.length}</span>
            </div>
          </div>
        )}
      </div>
      
      <style jsx>{`
        .docx-content {
          line-height: 1.6;
        }
        .docx-content p {
          margin-bottom: 1rem;
        }
        .docx-content h1, .docx-content h2, .docx-content h3, .docx-content h4, .docx-content h5, .docx-content h6 {
          margin-top: 1.5rem;
          margin-bottom: 0.5rem;
          font-weight: 600;
        }
        .docx-content ul, .docx-content ol {
          margin-bottom: 1rem;
          padding-left: 1.5rem;
        }
        .docx-content li {
          margin-bottom: 0.25rem;
        }
        .docx-content table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 1rem;
        }
        .docx-content td, .docx-content th {
          border: 1px solid #e5e7eb;
          padding: 0.5rem;
          text-align: left;
        }
        .docx-content th {
          background-color: #f9fafb;
          font-weight: 600;
        }
        @media (prefers-color-scheme: dark) {
          .docx-content td, .docx-content th {
            border-color: #374151;
          }
          .docx-content th {
            background-color: #1f2937;
          }
        }
      `}</style>
    </div>
  );
};
